import React, { useState, useEffect } from 'react';
import { MdClose, MdPerson, MdSave } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL as DEFAULT_API_BASE_URL } from '../../config/api';

const EditUserModal = ({ isOpen, onClose, user, showPopup, fetchData, API_BASE_URL, userMmdaId, onSuccess, onError }) => {
  // Use prop API_BASE_URL if provided, otherwise use default from config
  const apiBaseUrl = API_BASE_URL || DEFAULT_API_BASE_URL;
  const [formData, setFormData] = useState({
    status: 'active',
  });
  const [loading, setLoading] = useState(false);

  const config = {
    headers: {
      'Authorization': 'Basic ' + btoa('Techland:Techland@24'),
      'Content-Type': 'application/json',
    },
  };

  useEffect(() => {
    if (isOpen && user) {
      console.log('EditUserModal - User data received:', user);
      setFormData({
        status: user.status || 'active',
      });
    }
  }, [isOpen, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!user.role_id) {
        throw new Error('User role information is missing. Please refresh the page and try again.');
      }

      const updateData = {
        status: formData.status,
        // Keep existing user data unchanged
        mmda_id: user.mmda_id,
        role_id: user.role_id,
        full_name: user.full_name,
        email: user.email,
        phone: user.phone,
        // Don't include password_hash to avoid overwriting it
      };

      console.log('Sending user update data:', updateData);
      const response = await axios.put(`${apiBaseUrl}/users/${user.user_id}`, updateData, config);
      
      if (response.status === 200) {
        onClose();

        // Show success modal
        if (onSuccess) {
          onSuccess('Updated', 'User', user.full_name, `User status has been updated to ${formData.status}.`);
        } else {
          showPopup('User status updated successfully!', 'success');
          fetchData();
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);

      // Show error modal
      if (onError) {
        onError('Update', 'User', user.full_name, error.response?.data?.error || error.message);
      } else {
        showPopup(error.response?.data?.error || 'Failed to update user status', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      status: 'active',
    });
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 0,
        width: '90%',
        maxWidth: 500,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px 12px 0 0',
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 'bold',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <MdPerson size={24} style={{ color: '#ff9800' }} />
            Edit User Status
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666',
              padding: 4,
              borderRadius: 4,
            }}
          >
            <MdClose />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 24 }}>
          <form onSubmit={handleSubmit}>
            {/* User Information Display */}
            <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8 }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16 }}>
                User Information
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
                <div>
                  <strong>Name:</strong> {user.full_name}
                </div>
                <div>
                  <strong>Role:</strong> {(user.role_name || '').replace(/_/g, ' ')}
                </div>
                <div>
                  <strong>Email:</strong> {user.email}
                </div>
                <div>
                  <strong>Phone:</strong> {user.phone}
                </div>
              </div>
            </div>

            {/* Status Selection */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 14 }}>
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: 14,
                  outline: 'none',
                  backgroundColor: 'white',
                }}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <small style={{ color: '#666', fontSize: 12, marginTop: 4, display: 'block' }}>
                {formData.status === 'active' 
                  ? 'User can log in and access the system' 
                  : 'User will be blocked from accessing the system'
                }
              </small>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  borderRadius: 6,
                  backgroundColor: 'white',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 6,
                  backgroundColor: loading ? '#6c757d' : '#ff9800',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <MdSave size={16} />
                {loading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
